import "@fontsource-variable/inter/index.css";
import "./styles.css";

import { ApiClientError } from "@lumberset/api-client";
import { RoutePending, Toaster, TooltipProvider } from "@lumberset/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { routeTree } from "./routeTree.gen";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // Retrying cannot fix a refusal. Only retry what might be a blip.
      retry: (failures, error) =>
        failures < 2 &&
        !(error instanceof ApiClientError && error.status >= 400 && error.status < 500),
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  // A load that takes more than a moment shows a quiet placeholder instead of a blank page:
  // the router waits 200 ms before showing it, and it fades in over the next few hundred.
  defaultPendingComponent: RoutePending,
  defaultPendingMs: 200,
  defaultPendingMinMs: 300,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <RouterProvider router={router} />
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
